import { Col, Row } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeComponents } from '../../../../themes';
import useFormats from '../../../../utilities/useFormats';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import { type SharedDetailProps, colSpan } from './shared';

const VehicleDetailsPanel = ({ voucher }: SharedDetailProps) => {
    const { t } = useTranslation(['giftVoucherDetails', 'giftVoucherList']);
    const { FormFields } = useThemeComponents();
    const translatedString = useTranslatedString();

    const { formatAmountWithCurrency } = useFormats(
        voucher.module.company.currency,
        voucher.module.company?.roundings?.amount?.decimals ?? 0,
        0
    );

    const vehicle = useMemo(() => {
        if (!voucher.vehicle) {
            return null;
        }

        if (voucher.vehicle.__typename !== 'LocalVariant') {
            throw new Error('Unsupported vehicle type for gift voucher vehicle panel');
        }

        const variantDetails: { variantName?: string; price?: string } = {};

        if (
            voucher.stock?.__typename === 'MobilityStockInventory' &&
            voucher.stock?.inventory?.__typename === 'MobilityInventory'
        ) {
            variantDetails.variantName = voucher.stock.inventory.variant?.name
                ? translatedString(voucher.stock.inventory.variant.name)
                : '';

            const price = voucher.stock?.price ?? voucher.stock?.inventory?.variant?.vehiclePrice;
            variantDetails.price = price ? formatAmountWithCurrency(price) : '';
        }

        return {
            ...variantDetails,
            makeName: translatedString(voucher.vehicle.model.make.name),
            modelName: translatedString(voucher.vehicle.model.name),
            identifier: voucher.vehicle.identifier,
        };
    }, [formatAmountWithCurrency, translatedString, voucher.stock, voucher.vehicle]);

    return (
        <Row gutter={[14, 20]}>
            <Col {...colSpan}>
                <FormFields.DisplayField
                    {...t('giftVoucherDetails:fields.vehicle.make', {
                        returnObjects: true,
                    })}
                    value={vehicle.makeName}
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.DisplayField
                    {...t('giftVoucherDetails:fields.vehicle.model', {
                        returnObjects: true,
                    })}
                    value={vehicle.modelName}
                />
            </Col>

            <Col {...colSpan}>
                <FormFields.DisplayField
                    {...t('giftVoucherDetails:fields.vehicle.variant', {
                        returnObjects: true,
                    })}
                    value={vehicle.variantName}
                />
            </Col>

            <Col {...colSpan}>
                <FormFields.DisplayField
                    {...t('giftVoucherDetails:fields.vehicle.identifier', {
                        returnObjects: true,
                    })}
                    value={vehicle.identifier}
                />
            </Col>

            <Col {...colSpan}>
                <FormFields.DisplayField
                    {...t('giftVoucherDetails:fields.vehicle.price', {
                        returnObjects: true,
                    })}
                    value={vehicle.price}
                />
            </Col>
        </Row>
    );
};

export default VehicleDetailsPanel;
